import { showBladeToast } from "@/shared/toast";

type MarketplaceSaveSearchModalOptions = {
    marketplaceEquivalentUrl: string | null;
};

function csrfToken(): string {
    return (
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
            ?.content ?? ""
    );
}

function resolveSearchUrlToSave(
    marketplaceEquivalentUrl: string | null,
): string {
    if (marketplaceEquivalentUrl) {
        return `${window.location.origin}${marketplaceEquivalentUrl}`;
    }

    return decodeURIComponent(window.location.href);
}

function suggestTitleFromUrl(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const make = urlParams.get("menu[make]");
    const model = urlParams.getAll("refinementList[model][0]")[0];

    if (!make) {
        return "";
    }

    return model ? `${make} ${model}` : make;
}

function hasActiveSearchFilters(
    marketplaceEquivalentUrl: string | null,
): boolean {
    if (marketplaceEquivalentUrl) {
        return true;
    }

    const params = new URLSearchParams(window.location.search);
    return [...params.keys()].some((key) => key !== "page");
}

function setPanelVisible(
    modalCard: HTMLElement,
    panels: Record<string, HTMLElement | null>,
    activeKey: string,
): void {
    modalCard.dataset.panel = activeKey;

    for (const [key, panel] of Object.entries(panels)) {
        if (!panel) {
            continue;
        }

        panel.hidden = key !== activeKey;
    }
}

function closeModal(modal: HTMLElement): void {
    modal.hidden = true;
    document.body.style.overflow = "";
}

function openModal(modal: HTMLElement): void {
    modal.hidden = false;
    document.body.style.overflow = "hidden";
}

export function initMarketplaceSaveSearchModal({
    marketplaceEquivalentUrl,
}: MarketplaceSaveSearchModalOptions): void {
    const modal = document.getElementById("marketplace-save-search-modal");
    const form = document.getElementById(
        "marketplace-save-search-form",
    ) as HTMLFormElement | null;
    const titleInput = document.getElementById(
        "marketplace-save-search-title",
    ) as HTMLInputElement | null;
    const errorEl = document.getElementById("marketplace-save-search-error");

    const panels = {
        form: document.getElementById("marketplace-save-search-form-panel"),
        noFilters: document.getElementById("marketplace-save-search-no-filters"),
        success: document.getElementById("marketplace-save-search-success"),
    };

    if (!modal || !form || !titleInput) {
        return;
    }

    const modalCard = modal.querySelector<HTMLElement>(".save-search-modal__card");
    if (!modalCard) {
        return;
    }

    const resetModalState = (): void => {
        setPanelVisible(modalCard, panels, "form");
        if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = "";
        }
        titleInput.value = "";
    };

    modal.querySelectorAll("[data-close-modal]").forEach((el) => {
        el.addEventListener("click", () => {
            closeModal(modal);
            resetModalState();
        });
    });

    document.body.addEventListener(
        "click",
        (event) => {
            const btn = (event.target as Element | null)?.closest<HTMLButtonElement>(
                'button[data-testid="save-search-btn"]',
            );
            if (!btn || btn.hasAttribute("data-open-login")) {
                return;
            }

            event.preventDefault();

            if (!hasActiveSearchFilters(marketplaceEquivalentUrl)) {
                setPanelVisible(modalCard, panels, "noFilters");
                openModal(modal);
                return;
            }

            resetModalState();
            titleInput.value = suggestTitleFromUrl();
            openModal(modal);
            titleInput.focus();
        },
        { capture: true },
    );

    form.addEventListener("submit", (event) => {
        event.preventDefault();

        const title = titleInput.value.trim();
        if (!title) {
            if (errorEl) {
                errorEl.textContent = "Le titre de la recherche est obligatoire.";
                errorEl.hidden = false;
            }
            return;
        }

        void (async () => {
            const searchUrl = resolveSearchUrlToSave(marketplaceEquivalentUrl);

            try {
                const response = await fetch("/savedSearchs", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": csrfToken(),
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        search_url: searchUrl,
                        title,
                    }),
                });

                if (!response.ok) {
                    const payload = (await response.json().catch(() => null)) as {
                        message?: string;
                        errors?: Record<string, string[]>;
                    } | null;
                    const validationMessage =
                        payload?.errors?.search_url?.[0] ??
                        payload?.errors?.title?.[0];
                    const message =
                        validationMessage ??
                        payload?.message ??
                        "Impossible de sauvegarder la recherche.";

                    if (errorEl) {
                        errorEl.textContent = message;
                        errorEl.hidden = false;
                    } else {
                        showBladeToast(message, "error");
                    }
                    return;
                }

                setPanelVisible(modalCard, panels, "success");
            } catch {
                showBladeToast(
                    "Impossible de sauvegarder la recherche.",
                    "error",
                );
            }
        })();
    });
}
